import PlaybookReviewService from "./PlaybookReviewService";
import { postToAgents } from "./PlaybookApi";

const PlaybookCorrectionsService = {
	/**
	 * Where people correct a playbook, judgment by judgment, for every decision or only those
	 * about one statement line.
	 *
	 * @param {Object} params { playbookId, canonicalCode }
	 * @return {Promise<Object>} { points, recordCount, runCount, lines, canonicalCode }
	 */
	describeCorrections({ playbookId, canonicalCode = null }) {
		return postToAgents({ operationId: "describePlaybookCorrections", body: { playbookId, canonicalCode } });
	},

	/**
	 * Decision Records as JSON lines, with the chosen exclusions, for every decision or only those
	 * about one statement line. Administrators only; the export is recorded with who ran it.
	 *
	 * @param {Object} params { playbookId, canonicalCode, onlyFinal, leaveOutClients, leaveOutPeople }
	 * @return {Promise<Object>} { fileName, jsonl, rowCount, eligibleCount, isCapped }
	 */
	exportDecisions({ playbookId, canonicalCode = null, onlyFinal, leaveOutClients, leaveOutPeople }) {
		return postToAgents({ operationId: "exportPlaybookDecisions", body: { playbookId, canonicalCode, onlyFinal, leaveOutClients, leaveOutPeople } });
	},

	/**
	 * The statement lines a playbook's decisions are about, as choices for the Corrections tab's
	 * line filter: every line first, then each line by name with how many decisions are about it.
	 *
	 * @param {Object} params
	 * @param {Object} params.corrections { lines: [{ code, name, count }] }
	 * @return {Array<Object>} [{ value, label }]
	 */
	describeLineChoices({ corrections }) {
		const lineChoices = (corrections.lines || []).map(line => ({ value: line.code, label: `${line.name} (${line.count})` }));
		const choices = [{ value: "", label: "Every statement line" }, ...lineChoices];
		return choices;
	},

	/**
	 * The line that opens a playbook's Corrections tab: how many judgments over how many runs,
	 * or that none are recorded yet.
	 *
	 * @param {Object} params
	 * @param {Object} params.corrections { recordCount, runCount }
	 * @return {String}
	 */
	describeCorrectionsIntro({ corrections }) {
		if (corrections.recordCount === 0) return "No judgments recorded yet. They appear here after this playbook's next run.";
		const judgments = corrections.recordCount === 1 ? "1 judgment" : `${corrections.recordCount} judgments`;
		const runs = corrections.runCount === 1 ? "1 run" : `${corrections.runCount} runs`;
		return `${judgments} recorded over ${runs}. Each row is one kind of judgment; where people disagree, and why, is where this playbook needs work.`;
	},

	/**
	 * What an export produced, in words: how many records, how many ready to train on, and
	 * whether the file was cut at the limit.
	 *
	 * @param {Object} params
	 * @param {Object} params.result { rowCount, eligibleCount, isCapped }
	 * @return {String}
	 */
	describeExportResult({ result }) {
		const records = result.rowCount === 1 ? "1 record" : `${result.rowCount} records`;
		const capNote = result.isCapped ? `; the file stops at ${result.rowCount} — export one playbook at a time to get the rest` : "";
		return `Exported ${records}, ${result.eligibleCount} ready to train on${capNote}.`;
	},

	/**
	 * How often people agreed with one kind of judgment, in words, with the tone of the small
	 * dot beside it: green when nearly always, amber when often, red when often not, grey when
	 * nobody has reviewed one yet.
	 *
	 * @param {Object} params
	 * @param {Object} params.point { reviewed, agreed }
	 * @return {Object} { text, tone }
	 */
	describeAgreement({ point }) {
		if (point.reviewed === 0) return { text: "None reviewed yet", tone: "neutral" };
		const decidedCount = point.agreed + point.disagreed;
		const share = decidedCount > 0 ? point.agreed / decidedCount : 0;
		let tone = "danger";
		if (share >= 0.9) tone = "success";
		else if (share >= 0.7) tone = "warning";
		return { text: `${point.agreed} of ${decidedCount}`, tone };
	},

	/**
	 * The reasons people gave when they disagreed with one kind of judgment, most common first,
	 * in words with their counts.
	 *
	 * @param {Object} params
	 * @param {Object} params.point { reasons: [{ key, count }] }
	 * @return {String}
	 */
	describeDisagreementReasons({ point }) {
		if (point.reasons.length === 0) return "—";
		return point.reasons.map(reason => `${PlaybookReviewService.describeRejectReason({ category: reason.key })} (${reason.count})`).join(" · ");
	},
};

export default PlaybookCorrectionsService;
